import { useState } from "react";
import { Info, X, GripVertical, Clock, Wand2, Lock, Unlock, Plus, ArrowUpDown } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const Step = ({ num, title, desc }) => (
  <div className="flex gap-3 items-start">
    <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{num}</span>
    <div>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
    </div>
  </div>
);

const VisualExample = ({ children, caption }) => (
  <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 mt-3">
    <div className="space-y-2">{children}</div>
    {caption && <p className="text-[10px] text-slate-400 mt-2 text-center italic">{caption}</p>}
  </div>
);

const MockSlot = ({ label, time, color, items }) => (
  <div className={`rounded-lg border-2 border-dashed p-2 ${color}`}>
    <div className="flex items-center gap-1.5 mb-1">
      <span className="text-[10px] font-bold">{label}</span>
      <span className="text-[9px] text-slate-400">{time}</span>
    </div>
    <div className="space-y-1">
      {items.map((item, i) => (
        <div key={i} className="bg-white rounded-md px-2 py-1 flex items-center gap-1.5 text-[10px] border border-slate-100">
          <GripVertical className="w-2.5 h-2.5 text-slate-300" />
          <span className="font-medium text-slate-700">{item}</span>
        </div>
      ))}
      {items.length === 0 && <div className="text-[9px] text-slate-300 text-center py-1">Drop stops here</div>}
    </div>
  </div>
);

const MockTimeBlock = ({ time, name, duration, color }) => (
  <div className="flex gap-2 items-stretch">
    <div className="w-12 text-right">
      <span className="text-[10px] font-mono text-slate-400">{time}</span>
    </div>
    <div className="w-0.5 bg-slate-200 relative">
      <div className="absolute top-1 -left-1 w-2.5 h-2.5 rounded-full bg-sky-400 border-2 border-white" />
    </div>
    <div className={`flex-1 rounded-lg border-l-4 px-2 py-1.5 ${color}`}>
      <p className="text-[10px] font-semibold text-slate-700">{name}</p>
      <p className="text-[9px] text-slate-400">{duration}</p>
    </div>
  </div>
);

const MockQueueItem = ({ name, slot, locked, time }) => (
  <div className="bg-white rounded-lg border border-slate-200 px-2.5 py-1.5 flex items-center gap-2">
    <GripVertical className="w-3 h-3 text-slate-300" />
    <div className="flex-1">
      <p className="text-[10px] font-semibold text-slate-700">{name}</p>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-[9px] text-slate-400">{time}</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{slot}</span>
      </div>
    </div>
    {locked ? <Lock className="w-3 h-3 text-amber-500" /> : <Unlock className="w-3 h-3 text-slate-300" />}
  </div>
);

/* ───── HELP CONTENT PER SCHEDULER ───── */

const DragSlotHelp = () => (
  <div className="space-y-4">
    <p className="text-sm text-slate-600">Organize your day by dropping stops into time slots. Think of it like filling a daily planner.</p>
    <div className="space-y-3">
      <Step num={1} title="See your time slots" desc="Morning, Lunch, Afternoon, and Evening — each slot represents a chunk of your day." />
      <Step num={2} title="Tap a stop to move it" desc="Tap the stop name in the unassigned pool, then tap the slot you want it in. Or drag it directly." />
      <Step num={3} title="Remove if needed" desc="Tap the X on any placed stop to send it back to the unassigned pool." />
      <Step num={4} title="Switch days" desc="Use the day arrows at the top to plan multi-day birthdays." />
    </div>
    <VisualExample caption="Drag stops from 'Unassigned' into time slots">
      <MockSlot label="Morning" time="8am-11am" color="border-amber-200 bg-amber-50/50" items={["Starbucks", "Dunkin'"]} />
      <MockSlot label="Lunch" time="11am-2pm" color="border-orange-200 bg-orange-50/50" items={["Chipotle"]} />
      <MockSlot label="Afternoon" time="2pm-5pm" color="border-sky-200 bg-sky-50/50" items={[]} />
    </VisualExample>
  </div>
);

const TimelineHelp = () => (
  <div className="space-y-4">
    <p className="text-sm text-slate-600">A visual hour-by-hour timeline. See exactly when each stop happens and how long it takes.</p>
    <div className="space-y-3">
      <Step num={1} title="Tap an empty hour" desc="Tap the + button next to any open hour to add a stop there." />
      <Step num={2} title="Pick your stop" desc="A search panel pops up — pick from your itinerary stops or search by name." />
      <Step num={3} title="Adjust duration" desc="Use the +/- buttons to change how long you'll spend at each stop (5 min to 2 hours)." />
      <Step num={4} title="Remove stops" desc="Tap the X on any scheduled stop to free up that hour." />
    </div>
    <VisualExample caption="Each block shows the stop, time, and duration">
      <div className="space-y-1.5">
        <MockTimeBlock time="9:00am" name="Starbucks" duration="10 min" color="border-l-amber-400 bg-amber-50" />
        <MockTimeBlock time="10:00am" name="Chipotle" duration="15 min" color="border-l-red-400 bg-red-50" />
        <MockTimeBlock time="11:00am" name="Nothing Bundt Cakes" duration="12 min" color="border-l-pink-400 bg-pink-50" />
      </div>
    </VisualExample>
  </div>
);

const SmartQueueHelp = () => (
  <div className="space-y-4">
    <p className="text-sm text-slate-600">A flexible priority list with smart auto-scheduling. Lock favorites in place, then let the system optimize the rest.</p>
    <div className="space-y-3">
      <Step num={1} title="Review your queue" desc="All stops are listed with estimated times and time-of-day slots." />
      <Step num={2} title="Lock must-haves" desc="Tap the lock icon on stops you don't want moved. They'll stay fixed during auto-arrange." />
      <Step num={3} title="Change slots manually" desc="Tap a stop's time slot chip to reassign it (Morning, Lunch, Afternoon, Evening)." />
      <Step num={4} title="Auto-arrange" desc="Hit the magic wand button to auto-sort unlocked stops by category (coffee in AM, restaurants in PM)." />
      <Step num={5} title="Reorder" desc="Use the arrow buttons to move stops up or down in the queue." />
    </div>
    <VisualExample caption="Lock stops you care about, auto-arrange the rest">
      <div className="space-y-1.5">
        <MockQueueItem name="Starbucks" slot="Morning" locked={true} time="9:00am" />
        <MockQueueItem name="Chipotle" slot="Lunch" locked={false} time="11:25am" />
        <MockQueueItem name="Baskin-Robbins" slot="Afternoon" locked={false} time="2:00pm" />
      </div>
      <div className="flex justify-center mt-2">
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-violet-100 rounded-lg">
          <Wand2 className="w-3 h-3 text-violet-500" />
          <span className="text-[10px] font-semibold text-violet-600">Auto-Arrange</span>
        </div>
      </div>
    </VisualExample>
  </div>
);

const HELP_MAP = {
  "drag-slot": { title: "Drag & Slot", subtitle: "Easiest", content: DragSlotHelp },
  "timeline": { title: "Timeline", subtitle: "Visual", content: TimelineHelp },
  "smart-queue": { title: "Smart Queue", subtitle: "Flexible", content: SmartQueueHelp },
};

export default function SchedulerHelpSheet({ type = "drag-slot" }) {
  const [open, setOpen] = useState(false);
  const help = HELP_MAP[type];
  if (!help) return null;
  const Content = help.content;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="w-7 h-7 rounded-full bg-violet-100 hover:bg-violet-200 flex items-center justify-center transition-colors"
          data-testid={`scheduler-help-${type}`}
        >
          <Info className="w-4 h-4 text-violet-500" />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl pb-8">
        <SheetHeader className="text-left mb-4">
          <SheetTitle className="flex items-center gap-2">
            <span className="text-lg font-black text-slate-800">{help.title}</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-600">{help.subtitle}</span>
          </SheetTitle>
          <p className="text-xs text-slate-400">How to use this scheduler</p>
        </SheetHeader>
        <Content />
      </SheetContent>
    </Sheet>
  );
}
